import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { dmClient } from '../../services/dmClient';
import { useAuth } from '../../auth/AuthContext';
import LookupTable from '../lookup/LookupTable';
import LookupUpload from '../lookup/LookupUpload';
import '../lookup/lookup.css';

/**
 * Per-case lookup reference data (CaseId = :caseId). Fully independent from the
 * account-wide lookup data managed at /admin/lookup -- these rows are scoped to
 * this case only and never appear in account-wide queries.
 */
export default function CaseLookupPage() {
  const { caseId } = useParams();
  const { isAdmin, hasPermission } = useAuth();
  const canWrite = isAdmin || hasPermission('lookup.write');

  const [caseData, setCaseData] = useState(null);
  const [showUpload, setShowUpload] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  const loadCase = useCallback(async () => {
    try {
      const resp = await dmClient.get(`/denials/${caseId}`);
      setCaseData(resp.data?.data || resp.data || null);
    } catch (_e) {
      setCaseData(null);
    }
  }, [caseId]);

  useEffect(() => { loadCase(); }, [loadCase]);

  const caseLabel = caseData?.ExternalId || caseData?.externalId || caseId;

  return (
    <div className="lookup-page">
      <div className="lookup-upload__breadcrumb">
        <Link to="/denials">Denials</Link> &nbsp;/&nbsp;
        <Link to={`/denials/${caseId}`}>{caseLabel}</Link> &nbsp;/&nbsp; Lookups
      </div>

      <div className="lookup-header">
        <div>
          <h1>Case Lookup Data <span className="lookup-scope-tag">This case only</span></h1>
          <p>
            Lookup reference data scoped to case <strong>{caseLabel}</strong>. These records are independent
            from the account-wide lookup table and apply only to this case.
          </p>
        </div>
        {canWrite && (
          <div className="lookup-actions">
            <button type="button" className="lookup-btn" onClick={() => setShowUpload((v) => !v)}>
              {showUpload ? 'Hide Upload' : 'Upload File'}
            </button>
          </div>
        )}
      </div>

      {showUpload && canWrite && (
        <div className="lookup-upload__section">
          <h2>Upload case lookup file</h2>
          <LookupUpload
            caseId={caseId}
            onCommitted={() => setReloadKey((k) => k + 1)}
          />
        </div>
      )}

      <LookupTable caseId={caseId} canWrite={canWrite} reloadKey={reloadKey} />
    </div>
  );
}
